import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartPie,
  faChartLine,
  faUsers,
  faComments,
  faBrain,
  faMagic,
  faCrown,
  faChevronLeft,
  faChevronRight,
  faBell,
  faPlug,
  faBullhorn,
  faArrowTrendUp,
  faTableList,
  faBolt,
  faWaveSquare,
  faShareNodes,
  faDiagramProject,
  faFilter,
  faBullseye
} from '@fortawesome/free-solid-svg-icons';
import {
  faInstagram,
  faFacebook,
  faTwitter,
  faLinkedin,
  faTiktok
} from '@fortawesome/free-brands-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useTranslation } from 'react-i18next';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

type SectionKey = 'social-media' | 'web-site' | 'ads';

interface SidebarLinkConfig {
  label: string;
  to: string;
  icon: IconDefinition;
  badgeColor?: string;
}

interface SidebarGroupConfig {
  titleKey: string;
  links: SidebarLinkConfig[];
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  // @ts-ignore
  const { t } = useTranslation('sidebar');
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'tr';

  // Remove language prefix for section detection
  const pathWithoutLang = location.pathname.replace(/^\/(tr|en)/, '');
  const section: SectionKey = pathWithoutLang.startsWith('/web-site')
    ? 'web-site'
    : pathWithoutLang.startsWith('/ads')
      ? 'ads'
      : 'social-media';

  const sectionGroups: Record<SectionKey, SidebarGroupConfig[]> = {
    'social-media': [
      {
        titleKey: 'sidebar.main',
        links: [
          { label: t('overview'), to: '/social-media', icon: faChartPie },
          { label: t('analytics'), to: '/social-media/analytics', icon: faChartLine },
          { label: t('audience'), to: '/social-media/audience', icon: faUsers },
          { label: t('engagement'), to: '/social-media/engagement', icon: faComments }
        ]
      },
      {
        titleKey: 'sidebar.socialNetworks',
        links: [
          { label: 'Instagram', to: '/social-media/instagram', icon: faInstagram, badgeColor: 'bg-pink-400' },
          { label: 'Facebook', to: '/social-media/facebook', icon: faFacebook, badgeColor: 'bg-blue-500' },
          { label: 'Twitter', to: '/social-media/twitter', icon: faTwitter, badgeColor: 'bg-sky-400' },
          { label: 'LinkedIn', to: '/social-media/linkedin', icon: faLinkedin, badgeColor: 'bg-blue-600' },
          { label: 'TikTok', to: '/social-media/tiktok', icon: faTiktok, badgeColor: 'bg-white' }
        ]
      },
      {
        titleKey: 'sidebar.aiTools',
        links: [
          { label: t('aiInsights'), to: '/social-media/ai-predictions', icon: faBrain },
          { label: t('recommendations'), to: '/social-media/ai-recommendations', icon: faMagic }
        ]
      }
    ],
    'web-site': [
      {
        titleKey: 'sidebar.general',
        links: [
          { label: t('traffic'), to: '/web-site/traffic', icon: faChartLine },
          { label: t('engagement'), to: '/web-site/engagement', icon: faComments },
          { label: t('pages'), to: '/web-site/pages', icon: faTableList }
        ]
      },
      {
        titleKey: 'sidebar.trafficAndConversion',
        links: [
          { label: t('events'), to: '/web-site/events', icon: faBolt },
          { label: t('channels'), to: '/web-site/channels', icon: faWaveSquare },
          { label: t('socialToSite'), to: '/web-site/social-to-site', icon: faShareNodes },
          { label: t('attribution'), to: '/web-site/attribution', icon: faDiagramProject }
        ]
      },
      {
        titleKey: 'sidebar.aiTools',
        links: [
          { label: t('aiInsights'), to: '/web-site/ai-predictions', icon: faBrain },
          { label: t('recommendations'), to: '/web-site/ai-recommendations', icon: faMagic }
        ]
      }
    ],
    ads: [
      {
        titleKey: 'sidebar.general',
        links: [
          { label: t('adAnalytics'), to: '/ads', icon: faBullhorn }
        ]
      },
      {
        titleKey: 'sidebar.conversion',
        links: [
          { label: t('adsToSite'), to: '/ads/ads-to-site', icon: faArrowTrendUp },
          { label: t('funnel'), to: '/ads/funnel', icon: faFilter },
          { label: t('conversions'), to: '/ads/conversions', icon: faBullseye }
        ]
      },
      {
        titleKey: 'sidebar.aiTools',
        links: [
          { label: t('aiInsights'), to: '/ads/ai-predictions', icon: faBrain },
          { label: t('recommendations'), to: '/ads/ai-recommendations', icon: faMagic }
        ]
      }
    ]
  };

  const getCommonGroups = (section: SectionKey): SidebarGroupConfig[] => [
    {
      titleKey: 'sidebar.settings',
      links: [
        { label: t('accountSettings'), to: '/settings', icon: faUsers },
        { label: t('notifications'), to: '/notifications', icon: faBell },
        { label: t('integrations'), to: `/${section}/integrations`, icon: faPlug }
      ]
    }
  ];

  const groups = [...sectionGroups[section], ...getCommonGroups(section)];

  // Helper function to add language prefix to paths
  const getLocalizedPath = (path: string) => {
    return `/${currentLang}${path}`;
  };

  const isLinkActive = (to: string) => {
    const normalizedTo = to.replace(/\/+$/, '');
    const normalizedPath = pathWithoutLang.replace(/\/+$/, '');
    return normalizedPath === normalizedTo || normalizedPath.startsWith(`${normalizedTo}/`);
  };

  const handleLinkClick = () => {
    // Mobilde (768px altı) sidebar açıksa kapat
    if (window.innerWidth < 768 && isOpen) {
      onToggle();
    }
  };

  const activeLinkClasses = 'bg-purple-600/20 text-purple-400';
  const inactiveLinkClasses = 'text-white/70 hover:text-white hover:bg-white/5';

  return (
    <aside
      id="sidebar"
      className={`fixed left-0 top-16 bottom-0 backdrop-blur-xl bg-white/5 border-r border-white/10 z-40 transition-all duration-300 ease-in-out ${isOpen
        ? 'w-full md:w-64'
        : 'w-0 md:w-10'
        }`}
    >
      <div className="h-full flex flex-col">
        {/* Açık durumda tam sidebar */}
        {isOpen && (
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {groups.map((group) => (
              <div key={group.titleKey} className="space-y-2">
                <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wider">{t(group.titleKey)}</h3>
                <nav className="space-y-1">
                  {group.links.map((link) => {
                    const isActive = isLinkActive(link.to);
                    return (
                      <Link
                        key={link.to}
                        to={getLocalizedPath(link.to)}
                        onClick={handleLinkClick}
                        className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-all ${isActive ? activeLinkClasses : inactiveLinkClasses
                          }`}
                      >
                        <FontAwesomeIcon icon={link.icon} className="w-5" />
                        <span>{link.label}</span>
                        {link.badgeColor && (
                          <span className={`ml-auto w-2 h-2 rounded-full ${link.badgeColor}`}></span>
                        )}
                      </Link>
                    );
                  })}
                </nav>
              </div>
            ))}
          </div>
        )}

        {/* Kapalı durumda sadece iconlar */}
        {!isOpen && (
          <div className="flex-1 overflow-y-auto py-6 space-y-4">
            {groups.map((group) => (
              <div key={group.titleKey} className="space-y-2">
                <nav className="space-y-1">
                  {group.links.map((link) => {
                    const isActive = isLinkActive(link.to);
                    return (
                      <Link
                        key={link.to}
                        to={getLocalizedPath(link.to)}
                        onClick={handleLinkClick}
                        className={`w-full flex items-center justify-center p-2 rounded-lg transition-all ${isActive ? activeLinkClasses : inactiveLinkClasses
                          }`}
                        title={link.label}
                      >
                        <FontAwesomeIcon icon={link.icon} className="w-5" />
                      </Link>
                    );
                  })}
                </nav>
              </div>
            ))}
          </div>
        )}
        <button
          onClick={onToggle}
          className="absolute bottom-6 right-0 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg p-2 transition-all duration-200 flex items-center justify-center w-8 h-8 -mr-4 z-50 hidden md:flex"
          title={isOpen ? t('closeSidebar') : t('openSidebar')}
        >
          <FontAwesomeIcon
            icon={isOpen ? faChevronLeft : faChevronRight}
            className="text-white/60 hover:text-white transition-colors text-xs"
          />
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;